import {
  BucketDefType,
  BucketKind,
  IRangeBucket,
  Maybe,
} from '../../../../utils/apollo/graphqlSchema';
import {
  isNumberLargerThanOrEqualToZero,
  isPositiveIntegerLargerThanZero,
} from '../../../../utils/numbers';
import {
  IAutoRangeBinning,
  IBinning,
  IRangeBinning,
} from '../../../../redux/slices/oldBuilder/binning/types';
import { MAX_INTEGER } from '../../../../config/constants';
import { isNil } from 'lodash';
import { MaybeNil } from '../../../../utils/interfaces';
import { getExpandedRangeBuckets } from '../../../../utils/binnings';
import { defaultRangeBucket } from '../../../../redux/slices/oldBuilder/binning/defaults';

export const MAX_BIN_COUNT = 50;
export const INITIAL_BIN_COUNT = 3;
export const BINNING_PRECISION = 4;

export const isValidAutoRangeBucketInput = (
  min: Maybe<number>,
  max: Maybe<number>,
  binCount: Maybe<number>,
) => {
  if (isNil(min) || isNil(max) || isNil(binCount)) {
    return false;
  }

  const areNumbersValid =
    isNumberLargerThanOrEqualToZero(min) &&
    isNumberLargerThanOrEqualToZero(max) &&
    isPositiveIntegerLargerThanZero(binCount) &&
    max > min;

  if (!areNumbersValid) {
    return false;
  }
  const maxValue = Number(max);
  if (maxValue > MAX_INTEGER) {
    return false;
  }
  const binCountValue = Number(binCount);
  return binCountValue <= MAX_BIN_COUNT;
};

// checks if there are any overlaps in the range buckets. Returns an array of booleans
export const checkRangeBucketsOverlapAndError = (
  buckets: IRangeBucket[],
): boolean[] => {
  let overlaps: boolean[] = buckets.map(() => false);
  const isValidRowSequence = (rangeBucket: IRangeBucket) => {
    if (isNil(rangeBucket?.max) || isNil(rangeBucket?.min)) {
      return true;
    }
    return (
      rangeBucket.max >= 0 &&
      rangeBucket.min >= 0 &&
      // XXX KB-6346 - we now allow min == max
      Number(rangeBucket.max) >= Number(rangeBucket.min)
    );
  };
  for (let i = 0; i < buckets.length; i++) {
    // first interval, check error only
    if (i === 0) {
      if (!isValidRowSequence(buckets[i])) {
        // First interval, no overlap
        overlaps[i] = true;
      } else {
        overlaps[i] = false;
      }
      continue;
    }

    const prev = buckets[i - 1];
    const curr = buckets[i];

    if (
      (prev?.max && curr?.min && Number(prev.max) > Number(curr.min)) ||
      !isValidRowSequence(buckets[i])
    ) {
      // There is an overlap, return true
      overlaps[i] = true;
    } else {
      // There is no overlap
      overlaps[i] = false;
    }
  }

  return overlaps;
};

const isValidInput = (input: number | null) => {
  if (isNil(input)) {
    return false;
  }
  return input >= 0;
};

export const isValidBinning = (
  binning: MaybeNil<IBinning>,
): binning is IBinning => {
  if (!binning) {
    return false;
  }

  if (
    binning.kind === BucketKind.Enum &&
    binning.type === BucketDefType.Basic
  ) {
    const list = binning.customBuckets;
    const validList = list.filter((item) => item.values.length > 0);
    if (validList.length === 0) {
      return false;
    }
  }

  if (
    binning.kind === BucketKind.Range &&
    binning.type === BucketDefType.Basic
  ) {
    if (binning.isAutoRangeBucket) {
      const { min, max, binCount } = binning.autoRangeBucket;
      return isValidAutoRangeBucketInput(min, max, binCount);
    } else {
      // check empty
      const rangeBuckets = binning.customBuckets;
      // the buckets who has both input min and max value
      const validBuckets = rangeBuckets.filter(
        (bucket) => isValidInput(bucket.max) && isValidInput(bucket.min),
      );

      const incompleteBucket = rangeBuckets.find(
        (bucket) =>
          (isValidInput(bucket.min) && !isValidInput(bucket.max)) ||
          (!isValidInput(bucket.min) && isValidInput(bucket.max)),
      );

      // if all buckets are invalid (no min or max), return false.
      if (incompleteBucket !== undefined || validBuckets.length === 0) {
        return false;
      }

      // check for any max value exceeding db max value
      if (rangeBuckets.find((b) => b.max && b.max > MAX_INTEGER)) {
        return false;
      }

      // check overlap
      const overlaps = checkRangeBucketsOverlapAndError(rangeBuckets);
      const hasOverlap = overlaps.includes(true);
      if (hasOverlap) {
        return false;
      }
    }
  }

  return true;
};

export const convertToManualRangeBinning = (
  autorangeBinning: IAutoRangeBinning,
  populateWithDefaultBucketsIfInvalid: boolean,
): IRangeBinning => {
  const autoRangeBucket = autorangeBinning.autoRangeBucket;
  let rangeBuckets: IRangeBinning['customBuckets'] | null = null;
  if (
    isValidAutoRangeBucketInput(
      autoRangeBucket.min,
      autoRangeBucket.max,
      autoRangeBucket.binCount,
    )
  ) {
    rangeBuckets = getExpandedRangeBuckets(autoRangeBucket);
  } else if (populateWithDefaultBucketsIfInvalid) {
    rangeBuckets = [];
    // not valid, create empty buckets with default number of bucket rows.
    for (let i = 0; i < INITIAL_BIN_COUNT; i++) {
      rangeBuckets.push(defaultRangeBucket);
    }
  } else {
    throw new Error(
      "Can't convert autobinning to manual binning because autorange is invalid",
    );
  }

  return {
    type: BucketDefType.Basic,
    kind: BucketKind.Range,
    isAutoRangeBucket: false,
    customBuckets: rangeBuckets,
    updateKey: autorangeBinning.updateKey,
  } satisfies IRangeBinning;
};
